import PropTypes from 'prop-types';

import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import Helmet from '../../components/Helmet';
import WebDashboard from '../../layouts/web';
import { Box, Stack, Typography, Button } from '@mui/material';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import Image from '../../assets/img/Content.png';
import {
  AudioIcon,
  Calendar,
  Check,
  Documents,
  DotIcon,
  HousingIcon,
  Sheet,
  Tabs,
  User,
  UserProfile,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { CertificationCategory, CountryCode, LanguageCode } from '@classity-edtech/platform-utils';
import { useState, useRef, useEffect, useMemo } from 'react';
import Products from '../../components/Products';
import DeviceChecks from '../../components/DeviceChecks';
import BookingSummary from '../../components/BookingSummary';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import GoogleButton from '../../components/GoogleButton';
import { useProductEventsQuery } from '../../services/productEvents';
import { useGetProductBySlugQuery } from '../../services/products';
import { CircularProgress, generateImageAssestUrl, getCountry } from '@classity-edtech/ui-base';
import { useLanguagesQuery } from '../../services/languages';
import { useCurriculumQuery } from '../../services/curriculums';
import { useGetMyProfileQuery } from '../../services/users';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { webPaths } from '../../routes/paths';

type TabType = 'profile' | 'goals' | 'lessons' | 'device';

export interface ClassRoomProps extends MuiContainerProps {}

export const ClassRoom = ({ className }: ClassRoomProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const productSlug = params.slug;
  const language = LanguageCode.EN;
  const screens = useAppMediaQuery();
  const isSmallScreen = screens.isMobile || screens.isTablet;
  const [tab, setTab] = useState<TabType>('profile');
  const [expandedText, setExpandedText] = useState('');
  const [hasOverflow, setHasOverflow] = useState(false);
  const textRef = useRef<HTMLElement>(null);
  const { data: user, isLoading: loadingUser } = useGetMyProfileQuery();

  const { data: currLanguages, isLoading: isLanguagesLoading } = useLanguagesQuery();
  const sessionStart = useMemo(() => new Date().toISOString(), []);

  const { data: curriculum, isLoading: isCurriculumLoading } = useCurriculumQuery({
    code: CountryCode.NG,
    fields: 'educationalStages,institutions',
    sessionStart,
  });

  const { data: product, isLoading: loadingProduct } = useGetProductBySlugQuery({
    slug: productSlug,
  });

  const dateRange = useMemo(() => {
    return {
      earliest: dayjs().subtract(1, 'year').toISOString(),
      latest: dayjs().add(1, 'year').toISOString(),
    };
  }, []);

  const { data: productEvents, isLoading: loadingProductEvents } = useProductEventsQuery(
    {
      page: 1,
      limit: 300,
      productEventIds: product?.productEvents,
      ...dateRange,
    },
    {
      skip: !product?.productEvents?.length,
    },
  );

  useEffect(() => {
    if (textRef.current) {
      setHasOverflow(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, []);

  const handleTab = (tab: TabType) => {
    setTab(tab);
  };

  const handleSheetOpen = text => {
    setExpandedText(text);
  };

  const handleCloseSheet = () => setExpandedText('');

  const title = product?.name?.find(d => d.language === language)?.displayName;
  const description = product?.description?.find(d => d.language === language)?.text;
  const firstProductEvent = productEvents?.[0];
  const instructor = firstProductEvent?.attendees?.find(a => a.organizer)?.user;
  const profile = instructor?.profile;

  const userProfile = () => {
    const gradeLevel =
      profile?.preference?.gradeLevels?.map(
        g => curriculum?.educationalStages?.find(c => c._id === g)?.stage?.text,
      ) || [];
    const languages = instructor?.preferences?.knowsLanguages?.map(
      l => currLanguages?.find(lang => lang?.code === l)?.name?.text,
    );
    const experiences =
      profile?.experiences?.map(experience => {
        const institution = curriculum?.institutions?.find(
          inst => inst?._id === experience?.organizationId,
        );

        return {
          institution: institution?.name?.text,
          location:
            `${institution?.addresses?.[0]?.township || ''} ${getCountry(institution?.addresses?.[0]?.country || CountryCode.NG)}`.trim(),
          date: `${dayjs(experience.from).format('MMM YYYY')} - ${dayjs(experience.to).format('MMM YYYY')}`,
        };
      }) || [];

    const languageCert = profile?.certifications?.find(
      cert => cert.category === CertificationCategory.LANGUAGE,
    );
    const trainingCert = profile?.certifications?.find(
      cert => cert.category === CertificationCategory.TRAINING,
    );
    const academicCert = profile?.certifications?.find(
      cert => cert.category === CertificationCategory.ACADEMIC,
    );

    return {
      profile: {
        name: `${profile?.firstname || ''} ${profile?.lastname || ''}`.trim(),
        src: generateImageAssestUrl(profile?.avatar?.contentUrl, 1),
        rating: 4.9,
        followers: 14,
        badge: 'Classity',
        countryCode: CountryCode.NG,
        followAction: () => {},
        isBadgeActive: true,
      },
      bio: profile?.bio || '',
      bioContent: (
        <Box
          component='img'
          sx={{
            aspectRatio: '9/5',
            borderRadius: '8px',
            height: '150px',
            background: globalColours['$global-colour-palette-gray'],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            objectFit: 'cover',
          }}
          alt=''
          src={generateImageAssestUrl(profile?.avatar?.contentUrl, 1)}
        />
      ),
      gradeLevels: gradeLevel,
      languages: languages,
      qualifications: trainingCert
        ? [
            {
              title: 'Training Qualification',
              institution: trainingCert.issuedByName,
              date: trainingCert.periodOfStudyTo
                ? new Date(trainingCert.periodOfStudyTo).toLocaleDateString()
                : 'N/A',
            },
          ]
        : [],
      experiences,
      englishProficiencies: languageCert
        ? [
            {
              institution: languageCert.issuedByName,
            },
          ]
        : [],
      credentials: academicCert
        ? [
            {
              institution: academicCert.issuedByName,
              title: 'Academic Qualification',
              date: academicCert.periodOfStudyTo
                ? new Date(academicCert.periodOfStudyTo).toLocaleDateString()
                : 'N/A',
            },
          ]
        : [],
      tabs: isSmallScreen
        ? []
        : [
            {
              label: 'Profile',
              active: true,
              action: () => {},
            },
            {
              label: 'Reviews',
              active: false,
              action: () => {},
            },
          ],
    };
  };

  const lessons =
    productEvents?.reduce((acc, event) => {
      const startDate = dayjs(event.startTime.dateTime);
      const endDate = dayjs(event.endTime.dateTime);
      const monthYear = startDate.format('MMM YYYY');

      const existingMonth = acc.find(item => item.month === monthYear);
      const isDisabled = endDate.isBefore(dayjs());

      const lessonData = {
        appointmentDay: startDate.format('ddd'),
        appointmentDate: startDate.format('DD'),
        appointmentTime: `${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}`,
        appointmentLocation: event.isVirtual ? 'Online only' : 'Hybrid',
        appointmentInfo: title || 'Lesson',
        isDisabled,
        url: `/${webPaths.products}/${event.code}`,
        clickAction: isDisabled ? undefined : () => navigate(`/${webPaths.products}/${event.code}`),
      };

      if (existingMonth) {
        existingMonth.lessons.push(lessonData);
      } else {
        acc.push({
          month: monthYear,
          lessons: [lessonData],
        });
      }

      return acc;
    }, []) || [];

  const recordings = [{ date: 'Jan 2026' }];

  const PayForYouNotification = (
    <Box
      sx={{
        p: 0.5,
        background: globalColours['$global-colour-sentiment-info-default'],
        width: '100%',
      }}
    >
      <Typography variant='body3' sx={{ color: globalColours['$global-colour-base-contrast'] }}>
        <strong>Can't pay?</strong> Share the payment link at Checkout and let a relative pay for
        you!
      </Typography>
    </Box>
  );

  return (
    <>
      <Helmet />

      <GoogleButton useOneTap />

      <WebDashboard>
        {loadingProduct || isLanguagesLoading || isCurriculumLoading || loadingUser ? (
          <Box
            sx={{
              width: '100%',
              height: '80vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                px: 2,
                py: 3,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: '10px',
                borderRadius: '24px',
                background: globalColours['$global-colour-palette-gray-50'],
              }}
            >
              <Stack
                sx={{
                  gap: { xs: 1, md: 1.5 },
                  minWidth: { xs: 'fit-content', md: '400px' },
                  flex: 0.4,
                }}
              >
                <Typography variant={isSmallScreen ? 'subtitle3' : 'subtitle1'}>{title}</Typography>
                <Box>
                  <Typography
                    ref={textRef}
                    variant={isSmallScreen ? 'body3' : 'body1'}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 6,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {description}
                  </Typography>
                  {hasOverflow && (
                    <Button
                      variant='text'
                      size='small'
                      onClick={() => handleSheetOpen(description)}
                      sx={{ p: 0, mt: 0.5, textTransform: 'none', textDecoration: 'underline' }}
                    >
                      Read more
                    </Button>
                  )}
                </Box>
              </Stack>

              <Box sx={{ width: '100%', alignSelf: 'flex-end' }}>
                <Box
                  component='img'
                  sx={{
                    height: { xs: '22px', md: 'auto' },
                    width: '100%',
                    borderRadius: '30px',
                  }}
                  alt=''
                  src={Image}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 1,
                px: { xs: 0, md: 6.5 },
                gap: 1.5,
              }}
            >
              <HousingIcon sx={{ width: '20px', height: '20px' }} />
              <Typography variant={isSmallScreen ? 'body3SemiBold' : 'body1SemiBold'}>
                Second term of the 2025/2026 academic session
              </Typography>
            </Box>

            <Box sx={{ mt: 3, display: { xs: 'block', md: 'none' } }}>{PayForYouNotification}</Box>

            <Box sx={{ height: { xs: '24px', md: '40px' } }} />

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                gap: 5,
                pb: { xs: '88px', md: 0 },
                pl: { xs: 0, md: 6.5 },
              }}
            >
              <Box sx={{ width: { xs: '100%', md: '41.7dvw' } }}>
                <Tabs
                  sx={{ width: '100%' }}
                  tabElements={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: globalColours['$global-colour-interactive-secondary-default'],
                        borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          px: '16px',
                          cursor: 'pointer',
                          alignItems: { xs: 'center', md: 'flex-start' },
                          ...(tab === 'profile'
                            ? {
                                color: globalColours['$global-colour-interactive-secondary-active'],
                                borderBottom: `solid 1px ${globalColours['$global-colour-interactive-primary-active']}`,
                              }
                            : {}),
                        }}
                        onClick={() => handleTab('profile')}
                      >
                        <User
                          sx={{
                            width: { xs: '16px', md: '24px' },
                            height: { xs: '16px', md: '24px' },
                          }}
                        />
                        <Typography variant={isSmallScreen ? 'body1' : 'subtitle4'}>
                          Instructor
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          px: '16px',
                          cursor: 'pointer',
                          alignItems: { xs: 'center', md: 'flex-start' },
                          ...(tab === 'goals'
                            ? {
                                color: globalColours['$global-colour-interactive-secondary-active'],
                                borderBottom: `solid 1px ${globalColours['$global-colour-interactive-primary-active']}`,
                              }
                            : {}),
                        }}
                        onClick={() => handleTab('goals')}
                      >
                        <Documents
                          sx={{
                            width: { xs: '16px', md: '24px' },
                            height: { xs: '16px', md: '24px' },
                          }}
                        />
                        <Typography variant={isSmallScreen ? 'body1' : 'subtitle4'}>
                          Learning goals
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          px: '16px',
                          cursor: 'pointer',
                          alignItems: { xs: 'center', md: 'flex-start' },
                          ...(tab === 'lessons'
                            ? {
                                color: globalColours['$global-colour-interactive-secondary-active'],
                                borderBottom: `solid 1px ${globalColours['$global-colour-interactive-primary-active']}`,
                              }
                            : {}),
                        }}
                        onClick={() => handleTab('lessons')}
                      >
                        <Calendar
                          sx={{
                            width: { xs: '16px', md: '24px' },
                            height: { xs: '16px', md: '24px' },
                          }}
                        />
                        <Typography variant={isSmallScreen ? 'body1' : 'subtitle4'}>
                          Lessons
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          px: '16px',
                          cursor: 'pointer',
                          alignItems: { xs: 'center', md: 'flex-start' },
                          ...(tab === 'device'
                            ? {
                                color: globalColours['$global-colour-interactive-secondary-active'],
                                borderBottom: `solid 1px ${globalColours['$global-colour-interactive-primary-active']}`,
                              }
                            : {}),
                        }}
                        onClick={() => handleTab('device')}
                      >
                        <AudioIcon
                          sx={{
                            width: { xs: '16px', md: '24px' },
                            height: { xs: '16px', md: '24px' },
                            fill: 'transparent',
                          }}
                        />
                        <Typography variant={isSmallScreen ? 'body1' : 'subtitle4'}>
                          Device checks
                        </Typography>
                      </Box>
                    </Box>
                  }
                />

                {tab === 'profile' && (
                  <Box sx={{ px: { xs: 0, md: 2 }, py: 2, my: { xs: 0, md: 3 } }}>
                    <UserProfile {...userProfile()} />
                  </Box>
                )}

                {tab === 'lessons' && (
                  <Box sx={{ px: { xs: 0, md: 2 }, py: 2, my: { xs: 0, md: 3 } }}>
                    <Products
                      lessons={lessons}
                      recordings={recordings}
                      isLoading={loadingProductEvents}
                    />
                  </Box>
                )}

                {tab === 'goals' && (
                  <Box sx={{ px: { xs: 0, md: 2 }, py: 2, my: { xs: 0, md: 3 } }}>
                    <Typography variant='subtitle5'>Overview</Typography>
                    <Typography variant='body2'>
                      This lesson plan is designed for Senior Secondary School 3 (SS3) students to
                      introduce the fundamental concepts of calculus, focusing on the derivative as
                      a rate of change and the basic rules for differentiating algebraic terms.
                    </Typography>
                    <Stack sx={{ gap: 0.5, my: 2.5 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <DotIcon sx={{ width: '10px', height: '10px' }} />
                        <div>
                          <Typography variant='body3SemiBold'>Subject: </Typography>
                          <Typography variant='body3'>Mathematics</Typography>
                        </div>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <DotIcon sx={{ width: '10px', height: '10px' }} />
                        <div>
                          <Typography variant='body3SemiBold'>Theme: </Typography>
                          <Typography variant='body3'>Calculus</Typography>
                        </div>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <DotIcon sx={{ width: '10px', height: '10px' }} />
                        <div>
                          <Typography variant='body3SemiBold'>Topic: </Typography>
                          <Typography variant='body3'>
                            Differentiation of Algebraic Functions I
                          </Typography>
                        </div>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <DotIcon sx={{ width: '10px', height: '10px' }} />
                        <div>
                          <Typography variant='body3SemiBold'>Duration: </Typography>
                          <Typography variant='body3'>60 minutes</Typography>
                        </div>
                      </Box>
                    </Stack>

                    <Stack sx={{ mb: 2 }}>
                      <Typography variant='subtitle5'>Lesson Objectives</Typography>
                      <Typography variant='body2'>
                        By the end of this lesson, students will be able to:
                      </Typography>
                    </Stack>

                    <Stack sx={{ gap: 0.5, my: 2.5 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Check sx={{ width: '12px', height: '12px' }} />
                        <Typography variant='body3'>
                          Explain the concept of a derivative as the gradient of a curve.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Check sx={{ width: '12px', height: '12px' }} />
                        <Typography variant='body3'>
                          Differentiate simple algebraic functions from first principles.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Check sx={{ width: '12px', height: '12px' }} />
                        <Typography variant='body3'>
                          Apply the Power Rule to differentiate functions of the form y = ax
                          <sup>n</sup>
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}

                {tab === 'device' && (
                  <Box sx={{ px: { xs: 0, md: 2 }, py: 2, my: { xs: 0, md: 3 } }}>
                    <DeviceChecks lessons={lessons} recordings={recordings} />
                  </Box>
                )}
              </Box>

              <Box sx={{ width: { xs: '100%', md: '20dvw' } }}>
                <BookingSummary
                  prices={product?.prices || []}
                  products={productEvents}
                  user={{
                    userId: user?.id,
                    uid: user?.profile?.ids?.find(p => p?.name === 'uid')?.value,
                    email: user?.email,
                    firstname: user?.profile?.firstname,
                    lastname: user?.profile?.lastname,
                    notifications: user?.preferences?.notifications,
                  }}
                />
              </Box>
            </Box>
          </>
        )}
      </WebDashboard>

      <Sheet
        closeSheet={handleCloseSheet}
        openSheet={!!expandedText}
        body={<Typography variant='body2'>{expandedText}</Typography>}
      />

      {expandedText && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 5,
            backgroundColor: globalColours['$global-colour-background-dimmer'],
          }}
          onClick={handleCloseSheet}
        ></Box>
      )}
    </>
  );
};

ClassRoom.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ClassRoom.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(ClassRoom)(
  ({ theme }) => `
`,
);
